import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  PlayIcon,
  DocumentTextIcon,
  XMarkIcon,
  MusicalNoteIcon,
  ArrowDownTrayIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { downloadCallRecording } from '../../utils/recordingDownload';
import { CallAttempt } from '../../types/lead';
import { callService } from '../../services/callService';
import {
  RecordingFetchError,
  recordingErrorCopy,
  type RecordingErrorKind,
} from '../../utils/recordingErrors';
import AudioPlayer from '../UI/AudioPlayer';
import Modal from '../UI/Modal';
import LoadingSpinner from '../UI/LoadingSpinner';
import CallConnectTiming from './CallConnectTiming';

interface LeadCallHistoryProps {
  calls: CallAttempt[];
  loading?: boolean;
  /** Used for downloaded recording filenames */
  leadName?: string;
  /**
   * Gates the connect-timing panel. The trace behind it carries a snapshot of
   * every agent's availability, lock and WebRTC state at the moment of the
   * call, which is not an agent's business to read — and the endpoint refuses
   * non-admins anyway, so showing the control would only produce an error.
   */
  isAdmin?: boolean;
}

function resolveAgentName(call: CallAttempt): string | null {
  const name =
    call.agentName ??
    call.agent_name ??
    (call.captured_params && typeof call.captured_params === 'object'
      ? (call.captured_params.agentName as string | undefined) ??
        (call.captured_params.agent_name as string | undefined)
      : undefined);
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

function RecordingErrorMessage({
  kind,
  onRetry,
}: {
  kind: RecordingErrorKind;
  onRetry?: () => void;
}) {
  const copy = recordingErrorCopy(kind);
  return (
    <div
      className="flex flex-col items-center gap-2 rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 px-4 py-6 text-center"
      role="alert"
    >
      <MusicalNoteIcon className="h-8 w-8 text-amber-600/70 dark:text-amber-400/70" aria-hidden />
      <p className="text-sm font-medium text-foreground">{copy.title}</p>
      <p className="max-w-xs text-xs text-muted-foreground">{copy.detail}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 text-xs font-medium text-primary hover:underline"
        >
          Try again
        </button>
      )}
    </div>
  );
}

function formatSeconds(secs: number): string {
  if (!Number.isFinite(secs) || secs < 0) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDuration(started?: string, finished?: string): string {
  if (!started || !finished) return '—';
  const secs = Math.round((new Date(finished).getTime() - new Date(started).getTime()) / 1000);
  return formatSeconds(secs);
}

const LeadCallHistory: React.FC<LeadCallHistoryProps> = ({
  calls,
  loading,
  leadName,
  isAdmin,
}) => {
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  // Collapsed by default and fetched on open: one request per call row on
  // every sidebar open would be a burst of queries nobody asked for.
  const [timingCallId, setTimingCallId] = useState<string | null>(null);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState<number | null>(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState<RecordingErrorKind | null>(null);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [downloadingCallId, setDownloadingCallId] = useState<string | null>(null);
  const recordingBlobRef = useRef<string | null>(null);

  const activeCall = calls.find((c) => c.id === activeCallId);

  const revokeRecordingBlob = useCallback(() => {
    if (recordingBlobRef.current) {
      URL.revokeObjectURL(recordingBlobRef.current);
      recordingBlobRef.current = null;
    }
  }, []);

  const clearRecording = useCallback(() => {
    revokeRecordingBlob();
    setRecordingUrl(null);
    setRecordingDuration(null);
    setRecordingError(null);
    setActiveCallId(null);
  }, [revokeRecordingBlob]);

  useEffect(() => () => revokeRecordingBlob(), [revokeRecordingBlob]);

  const loadRecording = useCallback(
    async (call: CallAttempt) => {
      if (activeCallId === call.id) {
        clearRecording();
        return;
      }

      revokeRecordingBlob();
      setActiveCallId(call.id);
      setRecordingLoading(true);
      setRecordingUrl(null);
      setRecordingDuration(null);
      setRecordingError(null);

      try {
        const playbackUrl = await callService.getCallRecordingPlaybackUrl(call.id);
        recordingBlobRef.current = playbackUrl;
        setRecordingUrl(playbackUrl);
      } catch (err) {
        setRecordingUrl(null);
        setRecordingError(
          err instanceof RecordingFetchError ? err.kind : 'unavailable'
        );
      } finally {
        setRecordingLoading(false);
      }
    },
    [activeCallId, clearRecording, revokeRecordingBlob]
  );

  const loadTranscript = async (call: CallAttempt) => {
    if (call.transcript) {
      setTranscript(call.transcript);
      setShowTranscript(true);
      return;
    }
    try {
      const text = await callService.getCallTranscript(call.id);
      setTranscript(text);
      setShowTranscript(true);
    } catch {
      setTranscript('Transcript unavailable.');
      setShowTranscript(true);
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-sm text-muted-foreground">Loading call history...</div>;
  }

  if (calls.length === 0) {
    return <div className="py-8 text-center text-sm text-muted-foreground">No calls yet.</div>;
  }

  return (
    <>
      <p className="mb-3 rounded-md border border-border/80 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        Use <PlayIcon className="mx-0.5 inline h-3.5 w-3.5 align-text-bottom" aria-hidden /> to play or{' '}
        <ArrowDownTrayIcon className="mx-0.5 inline h-3.5 w-3.5 align-text-bottom" aria-hidden /> to download
        a call recording.
      </p>

      {activeCallId && (
        <div className="mb-4 rounded-lg border border-primary/30 bg-muted/20 p-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">Call Recording</p>
              {activeCall && (
                <p className="truncate text-xs text-muted-foreground">
                  Attempt #{calls.length - calls.findIndex((c) => c.id === activeCallId)}
                  {activeCall.started_at || activeCall.created_at
                    ? ` · ${new Date(activeCall.started_at || activeCall.created_at!).toLocaleString()}`
                    : ''}
                </p>
              )}
              {recordingDuration != null && (
                <p className="text-xs text-muted-foreground">
                  Recording duration: <span className="text-foreground">{formatSeconds(recordingDuration)}</span>
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              {activeCall && (
                <button
                  type="button"
                  disabled={downloadingCallId === activeCall.id}
                  onClick={() => {
                    setDownloadingCallId(activeCall.id);
                    void downloadCallRecording(
                      activeCall.id,
                      leadName || 'call-recording'
                    ).finally(() => setDownloadingCallId(null));
                  }}
                  className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                  title="Download recording"
                  aria-label="Download recording"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={clearRecording}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Close recording"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          {recordingLoading ? (
            <div className="flex items-center justify-center py-6">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-sm text-muted-foreground">Loading recording...</span>
            </div>
          ) : recordingUrl ? (
            <AudioPlayer
              key={recordingUrl}
              src={recordingUrl}
              className="w-full"
              onDuration={(secs) => setRecordingDuration(secs)}
              onError={() => {
                revokeRecordingBlob();
                setRecordingUrl(null);
                setRecordingError('playback');
              }}
            />
          ) : recordingError ? (
            <RecordingErrorMessage
              kind={recordingError}
              onRetry={
                activeCall ? () => void loadRecording(activeCall) : undefined
              }
            />
          ) : null}
        </div>
      )}

      <div className="space-y-2">
        {calls.map((call, idx) => {
          const isActive = activeCallId === call.id;
          const agentName = resolveAgentName(call);
          return (
            <div
              key={call.id}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                isActive ? 'border-primary/50 bg-primary/5' : 'border-border'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-foreground">Attempt #{calls.length - idx}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {agentName ? (
                      <span className="font-medium text-foreground/90">{agentName}</span>
                    ) : (
                      <span className="italic">Agent unknown</span>
                    )}
                    {' · '}
                    {call.started_at || call.created_at
                      ? new Date(call.started_at || call.created_at!).toLocaleString()
                      : '—'}
                    {' · '}
                    Call duration:{' '}
                    {formatDuration(
                      call.started_at ? String(call.started_at) : undefined,
                      call.finished_at ? String(call.finished_at) : undefined
                    )}
                  </p>
                  {call.reason && (
                    <p className="mt-1.5 flex items-center gap-1.5 text-xs">
                      <span className="text-muted-foreground">Outcome:</span>
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 font-medium capitalize text-primary">
                        {call.reason}
                      </span>
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => void loadRecording(call)}
                    className={`rounded-md border p-1.5 transition-colors ${
                      isActive
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:bg-accent'
                    }`}
                    title={isActive ? 'Hide recording' : 'Play recording in sidebar'}
                  >
                    <PlayIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    disabled={downloadingCallId === call.id}
                    onClick={() => {
                      setDownloadingCallId(call.id);
                      void downloadCallRecording(
                        call.id,
                        leadName ? `${leadName}-attempt-${calls.length - idx}` : undefined
                      ).finally(() => setDownloadingCallId(null));
                    }}
                    className="rounded-md border border-border p-1.5 hover:bg-accent disabled:opacity-50"
                    title="Download recording"
                    aria-label="Download recording"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => loadTranscript(call)}
                    className="rounded-md border border-border p-1.5 hover:bg-accent"
                    title="View transcript"
                  >
                    <DocumentTextIcon className="h-4 w-4" />
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() =>
                        setTimingCallId((prev) => (prev === call.id ? null : call.id))
                      }
                      className={`rounded-md border p-1.5 transition-colors ${
                        timingCallId === call.id
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:bg-accent'
                      }`}
                      title="Connect timing"
                      aria-label="Connect timing"
                    >
                      <ClockIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              {isAdmin && timingCallId === call.id && (
                <div className="mt-3">
                  <CallConnectTiming callAttemptId={call.id} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Modal isOpen={showTranscript} onClose={() => setShowTranscript(false)} title="Transcript">
        <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap text-sm">{transcript}</pre>
      </Modal>
    </>
  );
};

export default LeadCallHistory;
